import { CalendarIcon } from '@panwds/icons';
import { twMerge } from '@panwds/tailwindcss';
import { isValid } from 'date-fns';
import React, { useCallback, useEffect, useState, version } from 'react';
import { useIntl } from 'react-intl';
import { getDataTestId } from '../../../utils/utils.js';
import { Button } from '../../Button/Button.js';
import { Calendar } from '../Calendar/Calendar.js';
import {} from '../Calendar/types.js';
import { DateInput } from '../date_input/date_input.js';
import { useActiveDate } from '../hooks/useActiveDate.js';
import { useDateFormat } from '../hooks/useDateFormat.js';
import { useMinMaxDate } from '../hooks/useMinMaxDate.js';
import { MAX_DATE, MIN_DATE } from '../utilities/constants.js';
import { getInitialActiveDate, validateSelectedDate } from '../utilities/date.logic.js';
import { applyMessage, cancelMessage } from '../utilities/messages.js';
import { DateInputPickerLayout } from './date_input_picker_layout.js';
/**
 * Allows the user to navigate a calendar and select a date.
 *
 * The `DatePicker` includes the logic for picking dates, but not the container within which it will generally be used.
 * For most use cases, use the `DatePickerDialog`. Use the `DatePicker` directly when you need a different, custom container.
 *
 * ### Import Guide
 *
 * ```jsx
 * import { DatePicker } from "@panwds/react-ui";
 * ```
 */
export const DateInputPicker = ({ dataMetrics, dataTestId: propsDataTestId, initialActiveDate, initialDate = undefined, max: propsMax = MAX_DATE, min: propsMin = MIN_DATE, onApply, weekStartsOn = 0, disabled = false, dateFormat = 'MM/dd/yyyy', label, placeHolder, placeholder }) => {
    const { pattern } = useDateFormat(dateFormat);
    const { min, max } = useMinMaxDate({ min: propsMin, max: propsMax, pattern });
    const [activeDate, setActiveDate] = useActiveDate({
        initialActiveDate: getInitialActiveDate({ initialActiveDate, initialDate }),
        min,
        max
    });
    const [calendarSelected, setCalendarSelected] = useState();
    const [selectedDate, setSelectedDate] = useState();
    const [isOpen, setOpen] = useState(false);
    const intl = useIntl();
    const dataTestId = getDataTestId('date-input-picker', propsDataTestId);
    useEffect(() => {
        if (validateSelectedDate({ selectedDate: initialDate, min, max })) {
            setCalendarSelected(initialDate);
            setSelectedDate(initialDate);
            setActiveDate(initialDate);
        }
        else if (!initialDate) {
            setCalendarSelected(undefined);
            setSelectedDate(undefined);
        }
    }, [initialDate, min, max]);
    const handleSelectDate = (next) => {
        if (!next || !isValid(next)) {
            return;
        }
        setCalendarSelected(next);
        setSelectedDate(next);
        setActiveDate(next);
        onApply(next);
    };
    const onDateInputSubmit = (date) => {
        setCalendarSelected(date);
        setSelectedDate(date);
        setActiveDate(new Date(date));
        onApply(new Date(date));
    };
    const handleCalenderSelected = (date) => {
        setCalendarSelected(date);
    };
    const togglePopover = useCallback((e) => {
        // Between react 16 and 17 event propagation changed such that 17 and after we need to stop propagation of this event,
        // while for 16 we need to let the event propagate.
        if (!/^16/.test(version)) {
            e.stopPropagation();
        }
        setOpen(!isOpen);
    }, [isOpen, setOpen]);
    const suffix = (React.createElement("button", { type: "button", "data-testid": `${dataTestId}-calendar_icon`, onMouseDown: togglePopover, disabled: disabled, className: twMerge('twds-flex twds-items-center twds-border-none twds-bg-transparent twds-h-full twds-p-0 twds-pr-2 twds-outline-none', !disabled
            ? 'dark:twds-text-white hover:twds-cursor-pointer'
            : 'twds-text-disabled dark:twds-text-disabled hover:twds-cursor-not-allowed') },
        React.createElement(CalendarIcon, { className: twMerge(!disabled
                ? 'dark:twds-text-white hover:twds-cursor-pointer'
                : 'twds-text-disabled dark:twds-text-disabled hover:twds-cursor-not-allowed') })));
    const onCancelHandler = () => {
        if (!calendarSelected || !isValid(calendarSelected)) {
            setCalendarSelected(undefined);
            setActiveDate(activeDate);
            setOpen(false);
            return;
        }
        const previousDate = new Date(calendarSelected);
        setCalendarSelected(previousDate);
        setActiveDate(previousDate);
        setOpen(false);
    };
    return (React.createElement(DateInputPickerLayout, { dataMetrics: dataMetrics, dataTestId: dataTestId, open: isOpen, onCancel: onCancelHandler, actions: React.createElement(React.Fragment, null,
            React.createElement(Button, { onClick: onCancelHandler, disabled: disabled, dataTestId: `${dataTestId}-cancel_button` }, intl.formatMessage(cancelMessage)),
            React.createElement(Button, { appearance: "primary", onClick: () => {
                    handleSelectDate(calendarSelected);
                    setOpen(false);
                }, dataTestId: `${dataTestId}-apply_button`, disabled: disabled || !calendarSelected }, intl.formatMessage(applyMessage))), calendar: React.createElement(Calendar, { activeDate: activeDate, dataTestId: dataTestId, max: max, min: min, onActiveDateChange: setActiveDate, onSelectedDateChange: handleCalenderSelected, selectedDate: calendarSelected, weekStartsOn: weekStartsOn }), inputs: React.createElement(DateInput, { selectedDate: selectedDate, dataTestId: dataTestId, label: label, placeholder: placeholder ?? placeHolder, onSelectDate: onDateInputSubmit, formatPattern: pattern, inputProps: { suffix, dataTestId }, max: max, min: min, disabled: disabled }) }));
};
//# sourceMappingURL=date_input_picker.js.map